import { FC, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, padStart, set } from "lodash";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { Colors } from "../../../../configs/colors";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { ICartSession } from "../../../../typings/interfaces/pos/cart-session";
import {
  addSession,
  removeSession,
  updateCartSessionItem,
} from "../../../../redux/actions/cartAction";
import usePricing from "../../../../hooks/usePricing";
import { DeleteConfirmationModal } from "../../../common/delete-confirmation-modal/delete-confirmation-modal";
import { useParams } from "react-router-dom";
import { CustomButton } from "../../../atoms";
import { AssignPinTagModal } from "../assign-pin-tag-modal/assign-pin-tag-modal";
import { TicketNoItemsDialog } from "../ticket-validation-dialogs/ticket-validation-dialogs";
import { ICustomerBill } from "../../../../typings";

export interface IBillingSessionNavProps {
  editingBillId?: string;
}

const scrollAmount = 131 * 1.5 + 8;

const BillingSessionNav: FC<IBillingSessionNavProps> = ({ editingBillId }) => {
  const { drafts, cart, selectedSession, selectedServiceType, newSessionRequest } = useAppSelector(
    (state) => state.cart
  );
  const dispatch = useAppDispatch();
  const { categoryList } = usePricing();
  const { customer } = useAppSelector((state) => state.customer);

  const hiddenBoxRef = useRef<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [openNoItemsDialog, setOpenNoItemsDialog] = useState(false);
  const [wasSessionAddedFromModal, setWasSessionAddedFromModal] =
    useState(false);
  const params = useParams();
  const userId = Number(params.id);

  const filteredDraftedSessions = drafts.filter(
    (draft) => draft?.customer?.id === userId
  )[0]?.draftSessions;
  const [openAssignPinTagModal, setOpenAssignPinTagModal] = useState(false);

  const [pendingSession, setPendingSession] = useState<ICartSession | null>(
    null
  );

  const [handlebillnav, sethandlebillnav] = useState<boolean>(false);
  const [setRotate, setSetRotate] = useState<boolean>(true);

  useEffect(() => {
    if (filteredDraftedSessions?.length > 0) {
      const loadSessions = async () => {
        for (let i = 0; i < filteredDraftedSessions?.length; i++) {
          // async(filteredDraftedSessions[i]) => {
          await dispatch(addSession(filteredDraftedSessions[i]));
          // await dispatch(
          //   CartSliceAction.discardDraftedSession(draftedSessions[i])
          // );
        }
        // dispatch(
        //   CartSliceAction.setSelectedSession(
        //     filteredDraftedSessions[filteredDraftedSessions.length - 1]
        //   )
        // );
        await dispatch(
          CartSliceAction.setSelectedServiceType(
            filteredDraftedSessions[filteredDraftedSessions?.length - 1]
              ?.cartItems[0]?.serviceType ?? categoryList?.body[0]
          )
        );
        await dispatch(CartSliceAction.discardCustomerDraft(userId));
      };
      loadSessions();
    } else {
      addNewSession();
    }
    return () => {
      // if there is no any drafted session, then clear the selected session and sessions
      dispatch(CartSliceAction.clearSession());
    };
  }, []);

  // useEffect(() => {
  //   setTimeout(() => {
  //     sethandlebillnav(false);
  //   }, 2000);
  // }, [handlebillnav]);

  useEffect(() => {
    const handleScroll = () => {
      if (hiddenBoxRef.current) {
        const scrollLeft = hiddenBoxRef.current.scrollLeft;
        // const maxScroll =
        //   hiddenBoxRef.current.scrollWidth - hiddenBoxRef.current.clientWidth;
        setScrollPosition(scrollLeft);
      }
    };

    if (hiddenBoxRef.current) {
      hiddenBoxRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (hiddenBoxRef.current) {
        hiddenBoxRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const maxScroll = hiddenBoxRef.current
    ? hiddenBoxRef.current.scrollWidth - hiddenBoxRef.current.clientWidth
    : 1;

  const handleScrollLeft = () => {
    if (hiddenBoxRef.current) {
      const currentScrollLeft = hiddenBoxRef.current.scrollLeft;
      hiddenBoxRef.current.scrollTo({
        left: currentScrollLeft - scrollAmount,
        behavior: "smooth", // Use smooth behavior for scrolling
      });
    }
  };

  const handleScrollRight = () => {
    if (hiddenBoxRef.current) {
      const currentScrollLeft = hiddenBoxRef.current.scrollLeft;
      hiddenBoxRef.current.scrollTo({
        left: currentScrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleOnSelectSession = (session: ICartSession) => {
    dispatch(CartSliceAction.setSelectedSession(session));

    dispatch(
      CartSliceAction.setSelectedServiceType(
        session.cartItems[0]?.serviceType ||
        categoryList?.body.find((category) => category.showInMenu)
      )
    );
  };

  const handleOnClickNewSession = () => {
    let isEmptySession = true;
    for (const session of cart) {
      if (session.cartItems?.some((item) => item.finalized)) {
        isEmptySession = false;
        break;
      }
    }

    if (selectedSession?.cartItems.length === 0 || isEmptySession) {
      setOpenNoItemsDialog(true);
      return;
    }

    const session = selectedSession;

    const hasValidPinTag =
      !!session?.pinTag?.color &&
      Array.isArray(session.pinTag.tags) &&
      session.pinTag.tags.length > 0;

    const newSession: ICartSession = {
      sessionId: uuidv4(),
      cartItems: [],
      customerId: userId,
      pinTag: {
        color: null,
        tags: [],
        date: null,
      },
    };

    if (hasValidPinTag) {
      dispatch(addSession(newSession));
      dispatch(CartSliceAction.setSelectedSession(newSession));
      dispatch(
        CartSliceAction.setSelectedServiceType(
          categoryList?.body.find((category) => category.showInMenu)
        )
      );

      setTimeout(() => {
        sethandlebillnav(false);
      }, 1000);
    } else {
      setPendingSession(newSession);
      setOpenAssignPinTagModal(true);
    }
  };

  // Another screen (the Maximum Pieces dialog) asked for a new ticket. Only
  // requests made after this nav mounted count.
  const handledNewSessionRequestRef = useRef(newSessionRequest);
  useEffect(() => {
    if (newSessionRequest === handledNewSessionRequestRef.current) return;
    handledNewSessionRequestRef.current = newSessionRequest;
    if (editingBillId) return;
    sethandlebillnav(true);
    handleOnClickNewSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSessionRequest]);

  const handleAddNewSession = () => {
    if (selectedServiceType?.categoryName === "Alteration") {
      dispatch(
        CartSliceAction.setSelectedServiceType(
          categoryList?.body.find((category) => category.showInMenu)
        )
      );
    }
    addNewSession();
  };

  const handleOnClickRemoveSession = () => {
    removeCartSession();
    setDeleteDialogOpen(false);
  };

  const addNewSession = () => {
    if (userId) {
      const uuId = uuidv4();

      const newSession: ICartSession = {
        sessionId: uuId,
        cartItems: [],
        customerId: userId,
        pinTag: {
          color: null,
          tags: [],
          date: null,
        },
      };
      dispatch(addSession(newSession));
    }
  };

  const removeCartSession = () => {
    dispatch(removeSession());
  };

  // useEffect(() => {
  //   const currentsession = selectedSession?.sessionId;
  //   setSetSessionId(currentsession || "");
  // }, []);

  useEffect(() => {
    if (
      setRotate &&
      selectedSession?.pinTag?.color != null &&
      handlebillnav == true
    ) {
      setTimeout(() => {
        addNewSession();
        setSetRotate(false);
      }, 200);
      setTimeout(() => {
        setSetRotate(true);
        sethandlebillnav(false);
        console.log(handlebillnav);
      }, 1500);
    }
  }, [setRotate, selectedSession?.pinTag?.color]);

  return (
    <Box style={{ position: "relative", height: "2.21rem" }}>
      <DeleteConfirmationModal
        open={deleteDialogOpen}
        message="Are you sure you want to remove this session?"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleOnClickRemoveSession()}
      />
      <TicketNoItemsDialog
        open={openNoItemsDialog}
        customerName={customer ? `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim() : undefined}
        message="Please add item(s) to this ticket before starting a new one."
        onOk={() => setOpenNoItemsDialog(false)}
      />
      <AssignPinTagModal
        isOpen={openAssignPinTagModal}
        onClose={() => {
          setOpenAssignPinTagModal(false);
          sethandlebillnav(false);
        }}
        serviceTypeId={
          selectedSession?.cartItems[0]?.serviceType?.id ?? 0
        }
        itemsCount={
          selectedSession?.cartItems.reduce(
            (acc, item) => (item.finalized ? acc + item.qty : acc),
            0
          ) ?? 0
        }
        sessionId={selectedSession?.sessionId ?? ""}
        pinTagDraftSession={{
          sessionId: selectedSession?.sessionId ?? "",
          customerId: selectedSession?.customerId ?? 0,
          cartItems: selectedSession?.cartItems ?? [],
          pinTag: { color: null, tags: [], date: null },
        }}
        onSubmit={(updatedSession) => {
          if (!wasSessionAddedFromModal) {
            handleAddNewSession();
          }
          dispatch(addSession(updatedSession));
          dispatch(CartSliceAction.setSelectedSession(updatedSession));
          dispatch(
            CartSliceAction.setSelectedServiceType(
              updatedSession.cartItems[0]?.serviceType || null
            )
          );
          setWasSessionAddedFromModal(false);
          setOpenAssignPinTagModal(false);
          setPendingSession(null);
        }}
        setWasSessionAddedFromModal={setWasSessionAddedFromModal}
      />
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "row",
          gap: "0.57rem",
          width: "100%",
        }}
      >
        <IconButton
          onClick={handleScrollLeft}
          style={{
            background:
              scrollPosition === 0
                ? Colors.ACCENT_GRAY_DISABLED
                : Colors.MAIN_GRAY,
            color: Colors.WHITE,
            borderRadius: "0.57rem",
            height: "2.21428571rem",
            aspectRatio: "24/31",
            padding: "0",
          }}
          disabled={scrollPosition === 0}
        >
          <ChevronLeftIcon style={{ fontSize: "1.78571429rem" }} />
        </IconButton>
        <Box
          ref={hiddenBoxRef}
          sx={{
            flexGrow: 1,
            overflowX: "auto",
            overflowY: "hidden",
            display: "flex",
            flexWrap: "nowrap",
            gap: "0.57rem",
            width: "100%",
            transition: "all 0.375s ease-in-out",
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar-thumb": {
              display: "none",
            },
          }}
        >
          {!!!editingBillId &&
            cart.map((session, i) => {
              const serviceName = session.cartItems[0]?.serviceType?.categoryName || "SESSION";
              const sessionNumber = `-${padStart((i + 1).toString(), 3, "0")}`;
              return (
                <CustomButton
                  title={`${serviceName}${sessionNumber}`}
                  sx={{
                    background:
                      selectedSession?.sessionId === session.sessionId
                        ? Colors.MAIN_GRAY
                        : Colors.ACCENT_GRAY_DISABLED,
                    color: Colors.WHITE,
                    borderRadius: "0.57rem",
                    height: "2.21428571rem",
                    fontSize: "1.14285714rem",
                    minWidth: "9.35714286rem",
                    maxWidth: "9.35714286rem",
                    width: "9.35714286rem",
                    padding: "0.428571429rem 0.5rem",
                    textTransform: "Capitalize",
                    "&:hover": {
                      background: `${selectedSession?.sessionId === session.sessionId
                          ? Colors.MAIN_GRAY
                          : Colors.ACCENT_GRAY_DISABLED
                        } !important`,
                    },
                  }}
                  key={i}
                  onClick={() => handleOnSelectSession(session)}
                >
                  {/* One line: long service names are cut with "…", the number stays */}
                  <Box
                    component="span"
                    sx={{ display: "flex", minWidth: 0, maxWidth: "100%", whiteSpace: "nowrap" }}
                  >
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                      {serviceName}
                    </Box>
                    <Box component="span" sx={{ flexShrink: 0 }}>
                      {sessionNumber}
                    </Box>
                  </Box>
                </CustomButton>
              );
            })}
          {editingBillId && (
            <CustomButton
              sx={{
                background: Colors.MAIN_GRAY,
                color: Colors.WHITE,
                borderRadius: "0.57rem",
                height: "2.21428571rem",
                fontSize: "1.14285714rem",
                minWidth: "9.35714286rem",
                maxWidth: "9.35714286rem",
                width: "9.35714286rem",
                padding: "0.428571429rem 0.5rem",
                textTransform: "Capitalize",
                "&:hover": {
                  background: `${Colors.MAIN_GRAY} !important`,
                },
              }}
            // onClick={() => handleOnSelectSession(session)}
            >
              {editingBillId}
            </CustomButton>
          )}
        </Box>
        <IconButton
          onClick={handleScrollRight}
          style={{
            background:
              scrollPosition === maxScroll
                ? Colors.ACCENT_GRAY_DISABLED
                : Colors.MAIN_GRAY,
            color: Colors.WHITE,
            borderRadius: "0.57rem",
            height: "2.21428571rem",
            aspectRatio: "24/31",
            padding: "0",
          }}
          disabled={scrollPosition === maxScroll}
        >
          <ChevronRightIcon style={{ fontSize: "1.78571429rem" }} />
        </IconButton>
        <IconButton
          style={{
            background:
              !!editingBillId || selectedSession === null
                ? Colors.ACCENT_GRAY_DISABLED
                : Colors.MAIN_GRAY,
            color: Colors.WHITE,
            borderRadius: "0.57rem",
            height: "2.21428571rem",
            aspectRatio: "1/1",
            padding: "0",
          }}
          onClick={() => setDeleteDialogOpen(true)}
          disabled={!!editingBillId || selectedSession === null}
        >
          <DeleteIcon style={{ fontSize: "1.53571429rem" }} />
        </IconButton>
        <IconButton
          aria-label="New session"
          style={{
            background: !!editingBillId
              ? Colors.ACCENT_GRAY_DISABLED
              : Colors.MAIN_GRAY,
            color: Colors.WHITE,
            borderRadius: "0.57rem",
            height: "2.21428571rem",
            aspectRatio: "1/1",
            padding: "0",
          }}
          onClick={() => {
            sethandlebillnav(true);
            handleOnClickNewSession();
          }}
          disabled={!!editingBillId}
        >
          <AddIcon style={{ fontSize: "1.78571429rem" }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default BillingSessionNav;
